import React, {Component} from 'react';
import PropTypes from 'prop-types';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import {Form} from 'react-final-form';
import {scalePow} from 'd3-scale';
import {select} from 'd3-selection';
import {Link, Button, SaveButton, SelectField, Collapsable, ThemeContext} from '../../common';
import {
  NOMEDIASOURCE,
  VOLUME_METER_DELAY,
  AUDIO_LEVEL_THRESHOLD,
} from '../../../configs/constants';
import VideoServiceNG from '../../../utils/videoServiceNg';

class MediaConfiguration extends Component {
  static propTypes = {
    defaultAudioSource: PropTypes.string,
    defaultVideoSource: PropTypes.string,
    onClickBack: PropTypes.func.isRequired,
    onConfirmUserMedia: PropTypes.func.isRequired,
    barColor: PropTypes.string.isRequired,
  };

  static defaultProps = {
    defaultAudioSource: '',
    defaultVideoSource: '',
  };

  constructor(props) {
    super(props);
    const {defaultAudioSource, defaultVideoSource} = props;

    this.state = {
      audioSources: [],
      videoSources: [],
      mediaDiagnosticsError: null,
      isSubmitDisabled: true,
      isMediaAccessGranted: false,
    };

    this.deviceOptions = {
      audio: {deviceId: defaultAudioSource},
      video: {deviceId: defaultVideoSource},
    };

    this.videoServiceNg = new VideoServiceNG();
  }

  componentWillMount() {
    navigator.getMedia = (
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia
    );
  }

  async componentDidMount() {
    this.svgEl = select(this.svg)
      .append('svg')
      .append('g');

    await this.refreshDevices();
    await this.changeSelectedDevices();
    await this.updateMediaDiagnosticsResult();
  }

  componentWillUnmount() {
    this.stopRtc();
    if (this.requestId) {
      window.cancelAnimationFrame(this.requestId);
      delete this.requestId;
    }
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      delete this.animationTimeout;
    }
    delete this.svgEl;
  }

  onAudioSourceSelect = (deviceId) => {
    this.deviceOptions.audio.deviceId = deviceId;
    this.changeSelectedDevices();
  };

  onVideoSourceSelect = (deviceId) => {
    this.deviceOptions.video.deviceId = deviceId;
    this.changeSelectedDevices();
  };

  stopRtc() {
    this.videoServiceNg.stop();
  }

  gotDevices = (devices, isPermissionsGranted) => {
    const defaultMediaNames = {
      audioinput: 'Microphone',
      videoinput: 'Camera',
    };
    devices = devices.filter(item => item.deviceId && item.deviceId.length > 0);
    const {
      audioinput: audioSources = {},
      videoinput: videoSources = {},
    } = mapValues(groupBy(devices, 'kind'), list =>
      list.map(({deviceId, label, kind}, index) => ({
        value: deviceId,
        label: label || `${defaultMediaNames[kind]} #${index + 1}`,
      }))
    );
    this.setState({
      audioSources,
      videoSources,
      isMediaAccessGranted: isPermissionsGranted,
    });
    this.updateMediaDiagnosticsResult();
    if (videoSources && videoSources[0]) {
      const firstVideoDevice = videoSources.length && videoSources[0].value;
      if (!this.deviceOptions.video.deviceId) {
        this.deviceOptions.video.deviceId = firstVideoDevice;
      }
    }
    return devices;
  };

  refreshDevices = async () => {
    const devices = await this.videoServiceNg.getDevices();
    const isPermissionsGranted = this.videoServiceNg.isPermissionsGranted();
    this.gotDevices(devices, isPermissionsGranted);
  }

  drawLoop = () => {
    let width = 0;
    let height = 0;

    const {barColor} = this.props;
    const svgElement = select(this.svg).node();
    const shouldResize = () => {
      if (!svgElement) {
        return false;
      }

      const newWidth = svgElement.getBoundingClientRect().width;
      const newHeight = svgElement.getBoundingClientRect().height;

      return width !== newWidth || height !== newHeight;
    };

    const scale = scalePow().exponent(0.25).domain([1, 0]).range([0, width]);

    const resize = () => {
      width = svgElement.getBoundingClientRect().width;
      height = svgElement.getBoundingClientRect().height;
      scale.range([0, width]);

      if (this.svgEl) {
        this.svgEl.selectAll('text').remove();
        this.svgEl
          .append('text')
          .attr('x', width / 2)
          .attr('y', (height / 2) + 4)
          .attr('class', 'audio-svg__text')
          .text('Volume');
      }
    };

    if (this.svgEl) {
      this.svgEl.selectAll('*').remove();
      const bars = this.svgEl
        .selectAll('rect')
        .data([0])
        .enter()
        .append('rect')
        .attr('width', 0)
        .attr('height', height)
        .attr('fill', barColor);

      const renderStep = () => {
        const audioSource = this.deviceOptions.audio.deviceId !== NOMEDIASOURCE;
        const hasAudio = this.videoServiceNg.getLocalStream().hasAudio() && audioSource;
        let audioLevel = 0;

        if (hasAudio) {
          audioLevel = this.videoServiceNg.getLocalStream().getAudioLevel();
          if (shouldResize()) {
            resize();
          }
        }

        bars
          .data([audioLevel])
          .attr('height', height)
          .attr('width', d => scale(0) - scale(d));

        if (hasAudio) {
          this.animationTimeout = setTimeout(() => {
            this.requestId = window.requestAnimationFrame(renderStep);
          }, VOLUME_METER_DELAY);
        }
      };

      renderStep();
    }
  };

  resetStream = () => {
    this.videoServiceNg.getLocalStream().stop();
    this.videoServiceNg.getLocalStream().close();
  };

  changeSelectedDevices = async () => {
    this.setState({isSubmitDisabled: true});
    const streamHasAudio = this.deviceOptions.audio.deviceId !== NOMEDIASOURCE;
    const streamHasVideo = this.deviceOptions.video.deviceId !== NOMEDIASOURCE;
    const microphoneId = streamHasAudio
      ? this.deviceOptions.audio.deviceId
      : null;
    const cameraId = streamHasVideo ? this.deviceOptions.video.deviceId : null;
    this.resetStream();
    await this.videoServiceNg.createLocalStream({
      audio: streamHasAudio,
      video: streamHasVideo,
      microphoneId,
      cameraId,
    });
    this.videoServiceNg.getLocalStream().play('videoContainer');
    this.drawLoop();
    if (this.videoServiceNg.isPermissionsGranted()) {
      this.setState({isMediaAccessGranted: true});
      this.refreshDevices();
    } else {
      this.setState({isMediaAccessGranted: false});
    }
    this.updateMediaDiagnosticsResult();
  };

  updateMediaDiagnosticsResult = async () => {
    const {videoSources, audioSources, isMediaAccessGranted} = this.state;
    const vidSources = videoSources.length ? videoSources.length : 0;
    const audSources = audioSources.length ? audioSources.length : 0;
    const hasAudio = this.videoServiceNg.getLocalStream().hasAudio();
    const hasVideo = this.videoServiceNg.getLocalStream().hasVideo();

    const onResult = (message, disabled) => {
      this.setState({mediaDiagnosticsError: message, isSubmitDisabled: disabled});
    };

    onResult('', true);

    if (!isMediaAccessGranted) {
      onResult('Please grant access to camera and mic!', true);
      return;
    }

    if (vidSources === 0) {
      onResult('Please plug in camera!', true);
      return;
    }

    if (audSources === 0) {
      onResult('Please plug in mic!', true);
      return;
    }

    if (!hasVideo) {
      onResult('Please select camera!', true);
      return;
    }

    if (!hasAudio) {
      onResult('Please select mic!', true);
      return;
    }

    let micIsMuted = true;
    let i = 0;
    const timerId = setInterval(() => {
      i += 1;
      const audioLevel = this.videoServiceNg.getLocalStream().getAudioLevel();
      micIsMuted = audioLevel <= AUDIO_LEVEL_THRESHOLD;

      if (i >= 10 || !micIsMuted) {
        clearTimeout(timerId);

        if (micIsMuted) {
          onResult('Your mic is muted or has no input!', true);
        } else {
          onResult(null, false);
        }
      }
    }, 500);
  };

  renderLinks = () => {
    const {mediaDiagnosticsError} = this.state;
    if (!mediaDiagnosticsError) {
      return null;
    }
    return (
      <ThemeContext.Consumer>
        {theme => (
          <div className="row">
            <Collapsable text="If you have issues with video or audio, check this out" expanded={mediaDiagnosticsError} center>
              <div className="form__row">
                <Link target="_blank" href="https://support.google.com/chrome/answer/2693767?co=GENIE.Platform%3DDesktop&hl=en" color={theme.secondaryColor} text="If you use Chrome"/>
              </div>
              <div className="form__row">
                <Link target="_blank" href="https://support.apple.com/en-ke/guide/safari/ibrw7f78f7fe/13.0/mac/10.15" color={theme.secondaryColor} text="If you use Safari"/>
                {/* <Link target="_blank" href="https://help.doxy.me/en/articles/2427129-allow-access-to-camera-in-safari" color={theme.secondaryColor} text="If you use Safari"/> */}
              </div>
              <div className="form__row">
                <Link target="_blank" href="https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions" color={theme.secondaryColor} text="If you use FireFox"/>
              </div>
            </Collapsable>
          </div>
        )}
      </ThemeContext.Consumer>
    );
  };

  renderMediaDiagnosticsResult = () => {
    const {mediaDiagnosticsError} = this.state;
    if (!mediaDiagnosticsError) {
      return null;
    }
    return (
      <div style={{margin: 10}}>
        <span role="img" aria-label="warning">⚠️</span>
        <span style={{marginLeft: 10}}>{mediaDiagnosticsError}</span>
      </div>
    );
  }

  renderConfirmMediaForm = () => {
    const {onClickBack} = this.props;
    const {audioSources, videoSources, isSubmitDisabled} = this.state;

    return ({handleSubmit, submitting}) => (
      <form className="form form_m" onSubmit={handleSubmit}>
        <div className="confirm-user-media">
          <div className="media-selects">
            <SelectField
              className="video-source-select"
              name="videoSource"
              searchable={false}
              options={[
                ...videoSources,
              ]}
              onSelect={this.onVideoSourceSelect}
              rowGap="none"
            />

            <SelectField
              className="audio-source-select"
              name="audioSource"
              searchable={false}
              options={[
                ...audioSources,
              ]}
              onSelect={this.onAudioSourceSelect}
              rowGap="none"
            />
          </div>
          <div className="media-previews">
            <div
              ref={(video) => {
                this.videoContainer = video;
              }}
              id="videoContainer"
              className="video"
            />
            <svg
              ref={(el) => {
                this.svg = el;
              }}
              className="audio-svg bar"
            />
          </div>
        </div>
        {this.renderMediaDiagnosticsResult()}
        {this.renderLinks()}
        <div className="row row_center">
          <Button
            handler={() => {onClickBack();}}
            size="xl"
            text="Back"
          />
          <SaveButton
            size="xl"
            type="submit"
            text="Confirm"
            disabled={submitting || isSubmitDisabled}
          />
        </div>
      </form>
    );
  };

  render() {
    const {onConfirmUserMedia} = this.props;
    const {audioSources, videoSources} = this.state;
    const firstAudioDevice = audioSources.length && audioSources[0].value;
    const firstVideoDevice = videoSources.length && videoSources[0].value;

    return (
      <Form
        onSubmit={onConfirmUserMedia}
        changeSettings={this.changeSettings}
        initialValues={{
          audioSource: this.deviceOptions.audio.deviceId || firstAudioDevice,
          videoSource: this.deviceOptions.video.deviceId || firstVideoDevice,
        }}
        validateOnBlur={false}
      >
        {this.renderConfirmMediaForm()}
      </Form>
    );
  }
}

export default MediaConfiguration;